import { StackShim as Stack, StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import { ITag, NeutralColors, mergeStyles, mergeStyleSets, normalize } from '@fluentui/react';
import { Button, Checkbox, makeStyles } from '@fluentui/react-components';
import React from 'react';

interface IAgmTrackerStatusCounts {
  statusId: number;
  statusName: string;
  statusIndex: number;
  count: number;
}

const styles = mergeStyleSets({
  itemContent: [
    normalize,
    {
      paddingTop: 5,
      height: 40,
      borderBottom: `1px solid ${NeutralColors.gray30}`,
      overflow: 'hidden',
    },
  ],
  checkLabel: {
    label: {
      whiteSpace: 'nowrap',
    },
  },
  itemNameAndCount: {
    display: 'flex',
    alignItems: 'center',
  },
});

interface ICheckBoxItemProps {
  statuses: IAgmTrackerStatusCounts;
  itemIsSelected: boolean;
  onCheckBoxItemChanged: (statusId: number, checked: boolean) => void;
  colorMapping: (count: number) => string;
}

const useCheckBoxStyles = makeStyles({
  countItem: { padding: '5px' },
});

const CheckBoxItem = (props: ICheckBoxItemProps) => {
  const { statuses, itemIsSelected, onCheckBoxItemChanged, colorMapping } = props;
  const checkBoxClasses = useCheckBoxStyles();
  return (
    <div data-is-focusable className={styles.itemContent} key={statuses?.statusId}>
      <Stack
        horizontal
        grow
        className={mergeStyles({
          borderRight: `solid 3px ${colorMapping(statuses.count)}`,
        })}
      >
        <StackItem grow={3} className={styles.itemNameAndCount}>
          <Checkbox
            label={statuses?.statusName}
            checked={itemIsSelected ?? false}
            onChange={(e, data) => onCheckBoxItemChanged(statuses?.statusId, !!data.checked)}
            className={styles.checkLabel}
          />
        </StackItem>
        <StackItem className={checkBoxClasses.countItem}>{statuses?.count}</StackItem>
      </Stack>
    </div>
  );
};

interface IAgmTrackerStatusMetricSelectorProps {
  onStatusChange: (items: ITag[], key: string, title: string) => void;
  colorMapping: (count: number) => string;
  statusMetrics: IAgmTrackerStatusCounts[];
  selectedMetrics: ITag[];
}

const AgmTrackerStatusMetricSelector = (props: IAgmTrackerStatusMetricSelectorProps) => {
  const { onStatusChange, colorMapping, statusMetrics, selectedMetrics } = props;

  const onCheckBoxItemChanged = (statusId: number, checked: boolean): void => {
    const item = statusMetrics?.find(
      (status: IAgmTrackerStatusCounts) => status.statusId === statusId,
    );
    if (item && checked) {
      const selectedItemsCopy = structuredClone(selectedMetrics) ?? [];
      selectedItemsCopy.push({ key: item.statusId, name: item.statusName });
      onStatusChange(selectedItemsCopy, 'status', 'Agm Tracker Status');
    } else if (item && !checked) {
      const selectedItemsCopy = structuredClone(selectedMetrics)?.filter(
        (status) => status.key !== item.statusId,
      );
      onStatusChange(selectedItemsCopy, 'status', 'Agm Tracker Status');
    }
  };

  const statuses = statusMetrics?.map((status) => {
    return {
      statusId: status?.statusId,
      statusName: `${status?.statusIndex} - ${status?.statusName}`,
      statusIndex: status?.statusIndex,
      count: status?.count,
    };
  });

  return (
    <>
      {statuses?.map((status) => (
        <CheckBoxItem
          key={status?.statusId}
          statuses={status}
          itemIsSelected={selectedMetrics?.map((x) => x.key).includes(status?.statusId)}
          onCheckBoxItemChanged={onCheckBoxItemChanged}
          colorMapping={colorMapping}
        />
      ))}
      <div
        style={{
          display: 'flex',
          justifyContent: 'end',
          padding: '16px 0',
        }}
      >
        <Button onClick={() => onStatusChange([], 'status', '')}>Reset Status Selections</Button>
      </div>
    </>
  );
};

export default AgmTrackerStatusMetricSelector;
